import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface SubscriptionStatus {
  hasActiveSubscription: boolean;
  subscription: {
    id: string;
    planId: string;
    planName: string;
    planTier: string;
    creditsPerMonth: number;
    billingPeriod: string;
    currentPeriodEnd: string;
    cancelAtPeriodEnd: boolean;
    status: string;
  } | null;
  isLoading: boolean;
}

export function useSubscription() {
  const [status, setStatus] = useState<SubscriptionStatus>({
    hasActiveSubscription: false,
    subscription: null,
    isLoading: true,
  });

  const checkSubscription = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        setStatus({
          hasActiveSubscription: false,
          subscription: null,
          isLoading: false,
        });
        return;
      }

      const { data, error } = await supabase.functions.invoke("manage-subscription", {
        body: { action: "status" },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (error) throw error;

      setStatus({
        hasActiveSubscription: data.hasActiveSubscription,
        subscription: data.subscription,
        isLoading: false,
      });
    } catch (error) {
      console.error("Error checking subscription:", error);
      setStatus({
        hasActiveSubscription: false,
        subscription: null,
        isLoading: false,
      });
    }
  }, []);

  useEffect(() => {
    checkSubscription();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user) {
        checkSubscription();
      } else {
        setStatus({
          hasActiveSubscription: false,
          subscription: null,
          isLoading: false,
        });
      }
    });

    return () => subscription.unsubscribe();
  }, [checkSubscription]);

  const cancelSubscription = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error("Not authenticated");
    
    const { data, error } = await supabase.functions.invoke("manage-subscription", {
      body: { action: "cancel" },
      headers: { Authorization: `Bearer ${session.access_token}` }
    });

    if (error) throw error;
    
    if (data.code === "MANUAL_SUBSCRIPTION") {
      throw new Error("This subscription was created manually and cannot be modified through billing. Please contact support.");
    }
    
    await checkSubscription();
    return data;
  };

  const reactivateSubscription = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error("Not authenticated");
    
    const { data, error } = await supabase.functions.invoke("manage-subscription", {
      body: { action: "reactivate" },
      headers: { Authorization: `Bearer ${session.access_token}` }
    });

    if (error) throw error;
    
    if (data.code === "MANUAL_SUBSCRIPTION") {
      throw new Error("This subscription was created manually and cannot be modified through billing. Please contact support.");
    }
    
    await checkSubscription();
    return data;
  };

  const openBillingPortal = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error("Not authenticated");
    
    const { data, error } = await supabase.functions.invoke("manage-subscription", {
      body: { action: "portal" },
      headers: { Authorization: `Bearer ${session.access_token}` }
    });

    if (error) throw error;
    
    // Handle case where no Stripe customer exists yet
    if (data.code === "NO_CUSTOMER") {
      throw new Error("No billing history available yet. Your billing portal will be available after your first payment.");
    }
    
    if (data.url) {
      window.open(data.url, "_blank");
    }
  };

  const switchPlan = async (newPlanId: string, billingPeriod?: string) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error("Not authenticated");
    
    const { data, error } = await supabase.functions.invoke("manage-subscription", {
      body: { action: "switch", newPlanId, billingPeriod },
      headers: { Authorization: `Bearer ${session.access_token}` }
    });

    if (error) throw error;
    
    if (data.code === "MANUAL_SUBSCRIPTION") {
      throw new Error("This subscription was created manually and cannot be modified through billing. Please contact support.");
    }
    
    await checkSubscription();
    return data;
  };

  return {
    ...status,
    checkSubscription,
    cancelSubscription,
    reactivateSubscription,
    openBillingPortal,
    switchPlan,
  };
}
